import type { DemoStory, DemoStoryArtifact, DemoStoryMessage } from "~/routes/resources.cara-demo";
import type { ShowcaseLayout } from "@caratzon/integrations";
import { useT } from "~/i18n/context";
import { resolveRenderUrl } from "~/lib/utils";

type LayoutSection = ShowcaseLayout["sections"][number];

const SECTION_ASPECTS: Record<string, string> = {
  hero: "3/4",
  origin: "4/3",
  process: "4/3",
  details: "4/5",
  resolution: "4/3",
  gallery: "4/3",
  narrative: "4/3",
  making: "4/5",
};

type ArtifactLabels = Record<string, string>;

function toParagraphs(text: string): string[] {
  const explicit = text.split(/\n{2,}/);
  if (explicit.length > 1) {
    return explicit.map((p) => p.trim()).filter(Boolean);
  }
  return text
    .split(/(?<=[.!?])\s*(?=[A-Z][a-z])/)
    .reduce<string[]>((acc, sentence) => {
      const trimmed = sentence.trim();
      if (!trimmed) return acc;
      const last = acc[acc.length - 1];
      if (last && last.length < 400) {
        acc[acc.length - 1] = last + " " + trimmed;
      } else {
        acc.push(trimmed);
      }
      return acc;
    }, []);
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-center"
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: "0.62rem",
        letterSpacing: "0.24em",
        textTransform: "uppercase" as const,
        color: "var(--muted-foreground)",
      }}
    >
      {children}
    </h2>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="border border-border"
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: "0.58rem",
        letterSpacing: "0.2em",
        textTransform: "uppercase" as const,
        padding: "4px 12px",
        color: "var(--muted-foreground)",
      }}
    >
      {children}
    </span>
  );
}

function extractDesignSpecs(artifacts: DemoStoryArtifact[]): {
  jewelryType?: string;
  metal?: string;
  style?: string;
  stones?: Array<{ type: string; cut: string; count: number; setting: string }>;
  finishes?: string[];
  estimatedWeight?: number;
  estimatedPrice?: number;
} | null {
  // Look for present_design_specs, finalize_design, or BOM artifacts
  const specArtifacts = artifacts.filter(
    (a) => a.type === "present_design_specs" || a.type === "finalize_design",
  );
  const latest = specArtifacts[specArtifacts.length - 1];
  if (!latest?.data) {
    const bomArtifact = artifacts.find((a) => a.type === "bom");
    if (!bomArtifact?.data) return null;
    const dp = bomArtifact.data.designParameters as Record<string, unknown> | undefined;
    if (!dp) return null;
    return {
      jewelryType: dp.jewelryType as string | undefined,
      metal: dp.metal as string | undefined,
      style: dp.style as string | undefined,
    };
  }

  const d = latest.data;
  return {
    jewelryType: d.jewelryType as string | undefined,
    metal: d.metal as string | undefined,
    style: d.style as string | undefined,
    stones: d.stones as Array<{ type: string; cut: string; count: number; setting: string }> | undefined,
    finishes: d.finishes as string[] | undefined,
    estimatedWeight: d.estimatedWeightGrams as number | undefined,
    estimatedPrice: d.estimatedRetailPriceUsd as number | undefined,
  };
}

function extractBom(artifacts: DemoStoryArtifact[]): Record<string, unknown> | null {
  const bomArtifact = [...artifacts].reverse().find((a) => a.type === "bom");
  if (!bomArtifact?.data) return null;
  return bomArtifact.data;
}

function extractStructuralAnalysis(artifacts: DemoStoryArtifact[]): Record<string, unknown> | null {
  const sa = [...artifacts].reverse().find((a) => a.type === "structural_analysis");
  if (!sa?.data) return null;
  return sa.data;
}

function getConversationHighlights(conversation: DemoStoryMessage[]): DemoStoryMessage[] {
  if (conversation.length <= 6) return conversation;
  const highlights: DemoStoryMessage[] = [];
  if (conversation.length > 0) highlights.push(conversation[0]!);
  const withImages = conversation.filter((m) => m.imageUrls && m.imageUrls.length > 0);
  for (const m of withImages) {
    if (!highlights.includes(m)) highlights.push(m);
  }
  const userMessages = conversation.filter((m) => m.role === "user");
  for (const m of userMessages.slice(0, 3)) {
    if (!highlights.includes(m)) highlights.push(m);
  }
  if (conversation.length > 1) {
    const last = conversation[conversation.length - 1]!;
    if (!highlights.includes(last)) highlights.push(last);
  }
  return highlights.sort(
    (a, b) => (conversation.indexOf(a)) - (conversation.indexOf(b)),
  );
}

export function DemoStoryPage({
  story,
  onBack,
  onTryIt,
}: {
  story: DemoStory;
  onBack: () => void;
  onTryIt?: () => void;
}) {
  const { t } = useT();
  const s = t.demo.story;
  const artifactLabels = s.artifactLabels as ArtifactLabels;

  const sections: LayoutSection[] = story.layout?.sections ?? [];
  const storyImages = story.generatedImages ?? [];
  const specs = extractDesignSpecs(story.artifacts);
  const bom = extractBom(story.artifacts);
  const structural = extractStructuralAnalysis(story.artifacts);

  const renderArtifacts = story.artifacts.filter(
    (a) => (a.imageUrl || a.dataUrl) && !["gallery_share", "sketch_analysis"].includes(a.type),
  );

  const conversationHighlights = getConversationHighlights(story.conversation);
  const hasMaterials = specs?.metal || specs?.stones;

  return (
    <div data-theme="client" className="min-h-screen bg-background text-foreground">
      {/* Back nav */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6 sm:pt-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.72rem",
            letterSpacing: "0.06em",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          {s.backToGallery}
        </button>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 sm:pt-10 pb-6 sm:pb-8">
        <div className="text-center">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <Pill>Cara AI</Pill>
            {story.sketchDataUrl && <Pill>{s.fromSketch ?? "From Sketch"}</Pill>}
            {specs?.jewelryType && <Pill>{specs.jewelryType}</Pill>}
            {specs?.metal && <Pill>{specs.metal}</Pill>}
          </div>

          <h1
            className="font-display font-light tracking-tight"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.4rem)",
              lineHeight: 1.15,
              maxWidth: "20ch",
              marginInline: "auto",
            }}
          >
            {story.storyTitle ?? story.prompt}
          </h1>

          {story.description && (
            <p
              className="mt-4 text-muted-foreground mx-auto"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.88rem",
                maxWidth: "48ch",
                lineHeight: 1.7,
              }}
            >
              {story.description}
            </p>
          )}

          {!story.description && story.prompt && (
            <p
              className="mt-4 text-muted-foreground mx-auto"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.88rem",
                maxWidth: "48ch",
                lineHeight: 1.7,
              }}
            >
              {story.prompt}
            </p>
          )}

          {story.creatorName && (
            <div
              className="mt-5 flex flex-wrap items-center justify-center gap-4"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.75rem",
                color: "var(--muted-foreground)",
              }}
            >
              <span>
                {s.createdBy}{" "}
                <span className="text-foreground font-medium">{story.creatorName}</span>
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Hero image */}
      <section className="mx-auto max-w-2xl px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="relative" style={{ aspectRatio: "1/1" }}>
          <img
            src={story.heroDataUrl}
            alt={story.storyTitle ?? story.prompt}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "contain", mixBlendMode: "multiply" }}
          />
        </div>
      </section>

      {/* Sketch section */}
      {story.sketchDataUrl && (
        <section className="py-10 sm:py-16 bg-muted/30">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <SectionLabel>{s.theSketch}</SectionLabel>
            <div className="mt-8 grid gap-8 items-center md:grid-cols-2">
              <div className="relative overflow-hidden rounded-sm border border-border/30" style={{ aspectRatio: "4/3" }}>
                <img
                  src={story.sketchDataUrl}
                  alt="Original sketch"
                  className="absolute inset-0 h-full w-full object-contain p-4"
                  style={{ mixBlendMode: "multiply" }}
                />
              </div>
              <div>
                {story.creatorName && (
                  <p
                    className="text-muted-foreground mb-4"
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: "0.75rem",
                      fontStyle: "italic",
                    }}
                  >
                    {s.sketchCaption.replace("{name}", story.creatorName)}
                  </p>
                )}
                <p
                  className="text-foreground leading-relaxed"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.88rem",
                    lineHeight: 1.85,
                  }}
                >
                  {story.prompt}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Design Details */}
      {specs && hasMaterials && (
        <section className="py-10 sm:py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <SectionLabel>{s.theDetails ?? "The Details"}</SectionLabel>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {specs.jewelryType && (
                <DetailCard label={s.detailType ?? "Type"} value={specs.jewelryType} />
              )}
              {specs.metal && (
                <DetailCard label={s.detailMetal ?? "Metal"} value={specs.metal} />
              )}
              {specs.style && (
                <DetailCard label={s.detailStyle ?? "Style"} value={specs.style} />
              )}
              {specs.stones && specs.stones.length > 0 && (
                <DetailCard
                  label={s.detailStones ?? "Stones"}
                  value={specs.stones.map((st) => `${st.count}× ${st.cut} ${st.type}`).join(", ")}
                />
              )}
              {specs.finishes && specs.finishes.length > 0 && (
                <DetailCard label={s.detailFinish ?? "Finish"} value={specs.finishes.join(", ")} />
              )}
              {specs.estimatedWeight && (
                <DetailCard label={s.detailWeight ?? "Weight"} value={`${specs.estimatedWeight}g`} />
              )}
              {specs.estimatedPrice && (
                <DetailCard
                  label={s.detailPrice ?? "Est. Price"}
                  value={`$${specs.estimatedPrice.toLocaleString()}`}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Structural Analysis */}
      {structural && (
        <section className="py-10 sm:py-16 bg-muted/30">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <SectionLabel>{s.structuralAnalysis ?? "Structural Analysis"}</SectionLabel>
            <StructuralCard data={structural} />
          </div>
        </section>
      )}

      {/* Story narrative */}
      {story.storyTitle && story.storyText && (
        <section className="py-10 sm:py-16">
          <div className="mx-auto max-w-2xl px-4 sm:px-6">
            <SectionLabel>{s.theStory}</SectionLabel>
            <h2
              className="font-display font-light tracking-tight mt-8 mb-6 text-center"
              style={{ fontSize: "clamp(1.3rem, 2.5vw, 2rem)" }}
            >
              {story.storyTitle}
            </h2>
            <div
              className="text-foreground leading-relaxed"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.88rem",
                lineHeight: 1.85,
              }}
            >
              {toParagraphs(story.storyText).map((p, i) => (
                <p key={i} className={i > 0 ? "mt-5" : ""}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Showcase sections with generated images */}
      {sections.length > 0 && (
        <section className="py-10 sm:py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <SectionLabel>{s.showcase}</SectionLabel>
            <div className="mt-10 space-y-20">
              {sections.map((section, i) => {
                const img = storyImages.find((gi) => gi.imageId === section.imageId);
                const isReversed = i % 2 === 1;
                const aspect = SECTION_ASPECTS[section.type] ?? "4/3";
                return (
                  <div
                    key={section.imageId}
                    className={`grid gap-8 items-center md:grid-cols-2 ${isReversed ? "md:direction-rtl" : ""}`}
                  >
                    <div className={isReversed ? "md:order-2" : ""}>
                      {img ? (
                        <div className="relative overflow-hidden rounded-sm" style={{ aspectRatio: aspect }}>
                          <img
                            src={resolveRenderUrl(img.imageUrl)}
                            alt={section.headline}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div
                          className="overflow-hidden rounded-sm bg-muted"
                          style={{ aspectRatio: aspect }}
                        />
                      )}
                    </div>
                    <div className={isReversed ? "md:order-1" : ""}>
                      <h3
                        className="font-display font-light tracking-tight mb-4"
                        style={{ fontSize: "clamp(1.1rem, 2vw, 1.5rem)" }}
                      >
                        {section.headline}
                      </h3>
                      <p
                        className="text-muted-foreground leading-relaxed"
                        style={{
                          fontFamily: "var(--font-sans)",
                          fontSize: "0.85rem",
                          lineHeight: 1.8,
                        }}
                      >
                        {section.copy}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Artifacts gallery — all renders/sketches */}
      {renderArtifacts.length > 1 && (
        <section className="py-10 sm:py-16 bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <SectionLabel>{s.designJourney ?? "The Design Journey"}</SectionLabel>
            <div className="mt-8 grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {renderArtifacts.map((artifact, i) => {
                const imgSrc = artifact.imageUrl || artifact.dataUrl;
                if (!imgSrc) return null;
                const label = artifactLabels[artifact.type] ?? artifact.type;
                return (
                  <div key={i} className="group">
                    <div className="relative overflow-hidden rounded-sm border border-border/20" style={{ aspectRatio: "1/1" }}>
                      <img
                        src={imgSrc}
                        alt={label}
                        className="absolute inset-0 h-full w-full object-contain p-1"
                        style={{ mixBlendMode: "multiply" }}
                        loading="lazy"
                      />
                    </div>
                    <p
                      className="mt-2 text-center"
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.6rem",
                        letterSpacing: "0.18em",
                        textTransform: "uppercase" as const,
                        color: "var(--muted-foreground)",
                      }}
                    >
                      {label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Conversation highlights */}
      {conversationHighlights.length > 0 && (
        <section className="py-10 sm:py-16">
          <div className="mx-auto max-w-2xl px-4 sm:px-6">
            <SectionLabel>{s.theConversation ?? "The Conversation"}</SectionLabel>
            <div className="mt-8 space-y-4">
              {conversationHighlights.map((msg, i) => (
                <ConversationBubble key={i} message={msg} />
              ))}
              {story.conversation.length > conversationHighlights.length && (
                <p
                  className="text-center pt-2"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.72rem",
                    color: "var(--muted-foreground)",
                  }}
                >
                  {(s.moreMessages ?? "{n} more messages in this conversation").replace(
                    "{n}",
                    String(story.conversation.length - conversationHighlights.length),
                  )}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Creator attribution + CTA */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-16 sm:pb-24">
        <div
          className="border border-border p-6 sm:p-10 md:p-16 text-center"
          style={{ borderRadius: "2px" }}
        >
          {story.creatorName && (
            <>
              <div
                className="mx-auto mb-4 flex items-center justify-center rounded-full bg-muted"
                style={{ width: 56, height: 56 }}
              >
                <span
                  className="font-display font-light"
                  style={{ fontSize: "1.1rem", color: "var(--foreground)" }}
                >
                  {story.creatorName
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase() ?? "")
                    .join("")}
                </span>
              </div>
              <h3
                className="font-display font-light"
                style={{ fontSize: "1.1rem" }}
              >
                {story.creatorName}
              </h3>
              <p
                className="mt-1 mb-6"
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "0.65rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase" as const,
                  color: "var(--muted-foreground)",
                }}
              >
                {s.createdWith.replace("{event}", s.eventName)}
              </p>
            </>
          )}

          {onTryIt && (
            <button
              onClick={onTryIt}
              className="inline-flex items-center gap-2 px-6 py-3 transition-all duration-200 hover:brightness-110"
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontFamily: "var(--font-sans)",
                background: "var(--teal-sapphire)",
                color: "var(--teal-sapphire-fg, #fff)",
              }}
            >
              {s.tryIt}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function DetailCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border/40 px-4 py-3" style={{ borderRadius: "2px" }}>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.58rem",
          letterSpacing: "0.2em",
          textTransform: "uppercase" as const,
          color: "var(--muted-foreground)",
          marginBottom: "4px",
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.85rem",
          color: "var(--foreground)",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function ConversationBubble({ message }: { message: DemoStoryMessage }) {
  const isUser = message.role === "user";
  const truncated = message.content.length > 300
    ? message.content.slice(0, 300).trimEnd() + "…"
    : message.content;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-3 ${
          isUser
            ? "bg-foreground/5 rounded-2xl rounded-br-sm"
            : "bg-card border border-border/40 rounded-2xl rounded-bl-sm"
        }`}
      >
        <p
          className="mb-1"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase" as const,
            color: "var(--muted-foreground)",
          }}
        >
          {isUser ? "Designer" : "Cara"}
        </p>
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.82rem",
            lineHeight: 1.7,
            color: "var(--foreground)",
          }}
        >
          {truncated}
        </p>
        {message.imageUrls && message.imageUrls.length > 0 && (
          <div className="mt-2 flex gap-2 flex-wrap">
            {message.imageUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                className="h-20 w-20 object-cover rounded-sm border border-border/20"
                loading="lazy"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StructuralCard({ data }: { data: Record<string, unknown> }) {
  const findings = data.findings as Array<{ severity: string; area: string; finding: string; recommendation: string }> | undefined;

  if (!findings || !Array.isArray(findings) || findings.length === 0) return null;

  const severityColors: Record<string, string> = {
    pass: "oklch(0.55 0.15 145)",
    warning: "oklch(0.65 0.15 70)",
    critical: "oklch(0.55 0.2 25)",
  };

  const hasCritical = findings.some((f) => f.severity === "critical");
  const hasWarning = findings.some((f) => f.severity === "warning");
  const verdict = hasCritical ? "CRITICAL" : hasWarning ? "WARNING" : "PASS";
  const verdictColor = hasCritical ? severityColors.critical : hasWarning ? severityColors.warning : severityColors.pass;

  return (
    <div className="mt-8 border border-border/40 overflow-hidden" style={{ borderRadius: "2px" }}>
      <div
        className="px-5 py-3 flex items-center gap-2"
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.68rem",
          letterSpacing: "0.15em",
          textTransform: "uppercase" as const,
          fontWeight: 600,
          color: verdictColor,
        }}
      >
        <span
          className="h-2 w-2 rounded-full"
          style={{ background: verdictColor }}
        />
        {verdict}
      </div>
      {findings.map((f, i) => (
        <div key={i} className="px-5 py-2.5 border-t border-border/30 flex items-start gap-3">
          <span
            className="mt-0.5 h-1.5 w-1.5 rounded-full shrink-0"
            style={{
              background: severityColors[f.severity] ?? "var(--muted-foreground)",
            }}
          />
          <div>
            <p
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.78rem",
                fontWeight: 500,
                color: "var(--foreground)",
              }}
            >
              {f.area}
            </p>
            <p
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.72rem",
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              {f.finding}
            </p>
            {f.recommendation && (
              <p
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "0.68rem",
                  color: "var(--muted-foreground)",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  marginTop: "2px",
                }}
              >
                {f.recommendation}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
